class HeaderNavigation extends HTMLElement {
  constructor() {
    super();
    this.initializeComponents();
    this.setupEventListeners();
    this.setupDataTargetListeners();
  }

  initializeComponents() {
    this.images = this.querySelectorAll('img');
    this.videos = document.querySelectorAll('video');
    this.shadowElement = this.querySelector('div[shadow]');
    this.bgDropElement = this.querySelector('div[bgdrop]');
    this.firstLevelNavItems = this.querySelectorAll('ul[data-level="1"] > li');
    this.secondLevelNavItems = this.querySelectorAll('ul[data-level="2"] > li');
    this.thirdLevelNavItems = this.querySelectorAll('ul[data-level="3"] > li');
    this.imagesLoaded = false;
    this.isFirstLevelNavActive = false;
    this.isSecondLevelNavActive = false;
    this.activeDropdown = null;
    this.duration = 300;

    this.hamburgerBtn = this.querySelector('[btn-mobile][data-type="hamburger"]');
    this.searchCloseBtns = document.querySelectorAll('[btn-search="close"]');
    this.btn_mobile = this.querySelectorAll('[btn-mobile]');
    this.btn_country = this.querySelectorAll('[btn-country]');
    this.searchBtns = document.querySelectorAll('[btn-search="toggle"], [btn-search="open"]');
    this.search_input = document.querySelector('#searchbox input');
    this.previousScrollY = 0;
    this.notification = document.querySelector('[data-notification]');
    this.closeTimeout = null;
  }

  setupEventListeners() {
    this.setupNavListeners(
      this.firstLevelNavItems,
      this.onFirstLevelNavMouseOver.bind(this),
      this.onFirstLevelNavMouseOut.bind(this),
      this.onFirstLevelNavKeyDown.bind(this),
    );
    this.setupNavListeners(
      this.secondLevelNavItems,
      this.onSecondOrThirdLevelNavMouseOver.bind(this),
      this.onSecondOrThirdLevelNavMouseOut.bind(this),
      this.onSecondOrThirdLevelNavKeyDown.bind(this),
    );
    this.setupNavListeners(
      this.thirdLevelNavItems,
      this.onSecondOrThirdLevelNavMouseOver.bind(this),
      this.onSecondOrThirdLevelNavMouseOut.bind(this),
      this.onSecondOrThirdLevelNavKeyDown.bind(this),
    );
    document.addEventListener('keydown', this.onGlobalKeyDown.bind(this));
    this.setupShadowAndBgDropListeners();

    this.btn_mobile.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.dataset.type !== 'hamburger') {
          return;
        }
        window.dispatchEvent(new CustomEvent('header-navigation:mobile-toggle'));
      });
    });

    this.btn_country.forEach((button) => {
      button.addEventListener('click', () => {
        window.countryPopup();
      });
    });

    this.searchBtns.forEach((button) =>
      button.addEventListener('click', (e) => {
        this.handleSearchBtnClick(e);
        // The rail is in the DOM whenever the sliding menu ships, so its presence no longer signals
        // in-menu search — only enableSlidingNavSearch does. With it off the chrome search icon
        // opens the legacy overlay and the menu must close behind it (menu-closes-on-search).
        if (button.getAttribute('btn-search') === 'open' && !window.enableSlidingNavSearch) {
          window.dispatchEvent(new CustomEvent('header-navigation:mobile-close'));
        }
      }),
    );

    document.addEventListener('click', (e) => {
      if (e.target.closest('[btn-search="close"]')) this.handleSearchCloseBtnClick(e);
    });

    window.addEventListener('search:teleported', () => this.refreshSearchElements());

    window.addEventListener('search:close', () => {
      // Read before hideSearch clears it: header-entered search has no menu state
      // worth returning to, so dismissing it (e.g. Escape) closes the menu too.
      const closeMenu = window.Alpine?.store('searchEntry') === 'header';
      this.hideSearch();
      this.showHamburgerBtn();
      if (closeMenu) window.dispatchEvent(new CustomEvent('header-navigation:mobile-close'));
    });

    this.waitForElement('.wisp_count', (element) => {
      this.notification.setAttribute('data-notification', element.querySelector('span:first-child').innerText);

      // Observe changes in the wisp count
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          setTimeout(() => {
            const count = document.querySelector('.wisp_count')
              ? document.querySelector('.wisp_count').querySelector('span:first-child').innerText
              : 0;
            this.notification.setAttribute('data-notification', count);
          }, 100);
        });
      });

      observer.observe(document.querySelector('.wisp'), { childList: true });
    });
  }

  handleSearchCloseBtnClick(e) {
    this.hideSearch();
    this.showHamburgerBtn();
  }

  refreshSearchElements() {
    this.searchCloseBtns = document.querySelectorAll('[btn-search="close"]');
    this.search_input = document.querySelector('#searchbox input');
    const display = Alpine.store('isSearchOpen') ? '' : 'none';
    this.searchCloseBtns.forEach((button) => (button.style.display = display));
  }

  handleSearchBtnClick(e) {
    e.preventDefault();
    // Below 1024px (the teleport breakpoint) the search component lives inside
    // the mobile menu, so the header search icon routes into the in-menu search
    // instead of the overlay.
    if (window.enableSlidingNavSearch && window.innerWidth < 1024) {
      this.openMenuSearch();
      return;
    }
    this.toggleSearch();
    this.toggleHamburgerBtn();
  }

  /**
   * Open the mobile menu with search already active, in one gesture. Everything here
   * must stay synchronous: iOS Safari only raises the soft keyboard for a focus()
   * that runs inside the user's tap, and the input only becomes focusable once the
   * menu-open dispatch has flipped <mobile-menu> visible. searchEntry records that
   * search was entered from the header so the menu's dismiss control closes the
   * whole menu ("Close") rather than revealing the nav list ("Back").
   */
  openMenuSearch() {
    Alpine.store('searchEntry', 'header');
    Alpine.store('isSearchOpen', true);
    window.dispatchEvent(new CustomEvent('header-navigation:mobile-open'));
    document.querySelector('#searchbox input[name="q"]')?.focus();
  }

  toggleHamburgerBtn() {
    if (this.hamburgerBtn.style.display === 'none') {
      return this.showHamburgerBtn();
    }

    return this.hideHamburgerBtn();
  }

  showHamburgerBtn() {
    this.hamburgerBtn.style.display = '';
  }

  hideHamburgerBtn() {
    this.hamburgerBtn.style.display = 'none';
  }

  toggleSearch() {
    if (Alpine.store('isSearchOpen')) {
      return this.hideSearch();
    }

    return this.showSearch();
  }

  /**
   * Raise the soft keyboard within the same gesture (D-04). iOS Safari only honors
   * a programmatic focus() that runs synchronously inside the user's tap — no await,
   * rAF, or setTimeout. The overlay input sits inside an x-show root whose display
   * Alpine reconciles on the next microtask, so the input is still display:none here.
   * Reveal its overlay ancestor synchronously (store is already true; Alpine settles
   * to the same visible state on the next tick) so focus() lands on a visible element.
   */
  showSearch() {
    Alpine.store('isSearchOpen', true);
    this.searchCloseBtns.forEach((button) => (button.style.display = ''));
    if (window.enableSlidingNavSearch) {
      document.body.classList.add('search_open');
      const searchInput = document.querySelector('#searchbox input[name="q"]');
      if (searchInput) {
        const overlay = searchInput.closest('[x-show]');
        if (overlay) overlay.style.display = '';
        searchInput.focus();
      }
    }
  }

  hideSearch() {
    Alpine.store('isSearchOpen', false);
    Alpine.store('searchEntry', null);
    this.searchCloseBtns.forEach((button) => (button.style.display = 'none'));
    if (window.enableSlidingNavSearch) document.body.classList.remove('search_open');
  }

  setupDataTargetListeners() {
    const navItemsWithDataTarget = this.querySelectorAll('ul[data-level="2"] > li[data-target]');
    navItemsWithDataTarget.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        const targetId = item.getAttribute('data-target');
        const targetElement = this.querySelector(`div[data-target-id="${targetId}"]`);
        if (targetElement) {
          item.setAttribute('data-target-show', 'true');
          targetElement.classList.remove('opacity-0');
        }
      });
      item.addEventListener('mouseleave', () => {
        const targetId = item.getAttribute('data-target');
        const targetElement = this.querySelector(`div[data-target-id="${targetId}"]`);
        if (targetElement) {
          item.setAttribute('data-target-show', 'false');
          targetElement.classList.add('opacity-0');
        }
      });
    });
  }

  setupNavListeners(navItems, mouseOverHandler, mouseOutHandler, keyDownHandler) {
    navItems.forEach((item) => {
      item.addEventListener('click', () => {
        const button = item.querySelector('button');
        if (button && button.dataset.href) {
          window.location.href = button.dataset.href;
          return;
        }
      });
      item.addEventListener('mouseenter', () => mouseOverHandler(item));
      item.addEventListener('mouseleave', () => mouseOutHandler(item));
      const button = item.querySelector('button');
      if (button) {
        button.addEventListener('keydown', (e) => keyDownHandler(item, e));
      }
    });
  }

  setupShadowAndBgDropListeners() {
    this.shadowElement.addEventListener('mousemove', this.reset.bind(this));
    this.bgDropElement.addEventListener('mouseleave', this.reset.bind(this));
  }

  onFirstLevelNavMouseOver(item) {
    this.noScroll(true);
    this.updateNavItemsState(this.firstLevelNavItems, item);
    this.updateTheme(item);
    this.updateMegadropdown(item);
  }

  onFirstLevelNavMouseOut(item) {
    if (item.getAttribute('has-children') !== 'true') {
      this.toggleNavItem(item, false);
      this.reset();
    }
  }

  onFirstLevelNavKeyDown(item, e) {
    if (['Enter', ' '].includes(e.key)) {
      e.preventDefault();
      this.updateNavItemsState(this.firstLevelNavItems, item);
      this.updateTheme(item);
      this.updateMegadropdown(item);
      this.trapFocusIfNeeded(item);
    }
  }

  onSecondOrThirdLevelNavMouseOver(item) {
    this.toggleNavItem(item, true);
    if (!item.querySelector('button[aria-controls]')) {
      return;
    }
    const target = item.querySelector('button[aria-controls]').getAttribute('aria-controls');
    this.fadeIn(this.querySelector(`#${target}`));
  }

  onSecondOrThirdLevelNavMouseOut(item) {
    this.toggleNavItem(item, false);
    if (!item.querySelector('button[aria-controls]')) {
      return;
    }
    const target = item.querySelector('button[aria-controls]').getAttribute('aria-controls');
    this.fadeOut(this.querySelector(`#${target}`));
  }

  onSecondOrThirdLevelNavKeyDown(item, e) {
    if (['Enter', ' '].includes(e.key)) {
      e.preventDefault();
      this.isSecondLevelNavActive = true;
      this.updateNavItemsState(this.secondLevelNavItems, item.closest('li'));
      if (!item.closest('li').querySelector('button[aria-controls]')) {
        return;
      }
      const target = item.closest('li').querySelector('button[aria-controls]').getAttribute('aria-controls');
      this.secondLevelNavItems.forEach((item) => {
        if (!item.querySelector('button[aria-controls]')) {
          return;
        }
        const target = item.querySelector('button[aria-controls]').getAttribute('aria-controls');
        this.fadeOut(this.querySelector(`#${target}`));
      });
      this.fadeIn(this.querySelector(`#${target}`));
      this.trapFocus(target);
      this.querySelector(`#${target}`).querySelector('ul > li > div:first-child a[href]').focus();
    }
  }

  onGlobalKeyDown(e) {
    if (e.key === 'Escape') {
      if (this.isFirstLevelNavActive && this.isSecondLevelNavActive) {
        this.focusNavItem(this.secondLevelNavItems);
        this.isSecondLevelNavActive = false;
        this.resetNavItemsState(this.secondLevelNavItems);
        this.secondLevelNavItems.forEach((item) => {
          if (!item.querySelector('button[aria-controls]')) {
            return;
          }
          const target = item.querySelector('button[aria-controls]').getAttribute('aria-controls');
          this.fadeOut(this.querySelector(`#${target}`));
        });
        return;
      }
      this.focusNavItem(this.firstLevelNavItems);
      this.reset();
    }
  }

  updateNavItemsState(navItems, activeItem) {
    navItems.forEach((item) => item.setAttribute('open', 'false'));
    activeItem.setAttribute('open', 'true');
  }

  updateTheme(item) {
    const theme = item.dataset.theme || '';
    this.setAttribute('data-theme', theme);
  }

  updateMegadropdown(item) {
    const newDropdown = this.querySelector(`#${item.dataset.id}`);

    if (this.activeDropdown !== newDropdown && this.activeDropdown) {
      this.fadeOut(this.activeDropdown);
    }

    if (item.getAttribute('has-children') === 'true') {
      this.activeMegadropdown(true);
      this.setAttribute('data-active', item.dataset.index);
      if (newDropdown) {
        this.fadeIn(newDropdown);
      }
    } else {
      this.activeMegadropdown(false);
      this.setAttribute('data-active', '0');
    }

    this.activeDropdown = newDropdown;
  }

  hideAllMegadropdowns() {
    this.querySelectorAll('div[id*="_desktop-dropdown"]').forEach((dropdown) => {
      this.fadeOut(dropdown);
    });
  }

  toggleNavItem(item, open) {
    item.setAttribute('open', open ? 'true' : 'false');
  }

  trapFocusIfNeeded(item) {
    if (item.getAttribute('has-children') === 'true') {
      const target = document.querySelector(`#${item.dataset.id}`);
      target.querySelector('& > div:first-child > ul > li > div:first-child a[href]').focus();
      this.trapFocus(item.dataset.id);
    }
  }

  focusNavItem(navItems) {
    navItems.forEach((item) => {
      if (item.getAttribute('open') === 'true') {
        item.querySelector('button').focus();
      }
    });
  }

  resetNavItemsState(navItems) {
    navItems.forEach((item) => item.setAttribute('open', 'false'));
  }

  reset() {
    this.isFirstLevelNavActive = false;
    this.isSecondLevelNavActive = false;
    this.noScroll(false);
    this.resetNavItemsState(this.firstLevelNavItems);
    this.resetNavItemsState(this.secondLevelNavItems);
    this.setAttribute('data-theme', '');
    this.activeMegadropdown(false);
    this.hideAllMegadropdowns();
    this.activeDropdown = null;
  }

  activeMegadropdown(show) {
    if (show && !this.isFirstLevelNavActive) {
      this.fadeIn(this.shadowElement);
      this.fadeIn(this.bgDropElement);
    } else if (!show) {
      this.fadeOut(this.shadowElement);
      this.fadeOut(this.bgDropElement);
    }

    this.isFirstLevelNavActive = show;
    this.loadAllImagesIfNeeded(show);

    if (document.querySelector('header-navigation[data-style="dynamic"]') && window.location.pathname === '/') {
      if (show) {
        clearTimeout(this.closeTimeout);
        this.setAttribute('open', 'desktop');
      } else {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = setTimeout(() => {
          this.setAttribute('open', 'false');
        }, 300);
      }

      return;
    }

    this.setAttribute('open', show ? 'desktop' : 'false');
  }

  loadAllImagesIfNeeded(show) {
    if (show && !this.imagesLoaded) {
      this.images.forEach((img) => img.setAttribute('loading', 'eager'));
      this.imagesLoaded = true;
    }
  }

  /**
   * Toggle scroll lock on the body element.
   * - Wrapped in a `setTimeout` to ensure the scroll lock is applied after the
   *   current event loop and does not block rendering the menu open and closed.
   * - Using `setTimeout` like this has a positive impact on INP performance.
   * @param {Boolean} enable whether scroll lock should be enabled or disabled
   */
  noScroll(enable) {
    setTimeout(() => {
      if (enable) {
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = '';
      }

      this.toggleVideos(!enable);
      this.toggleThirdPartyElements(enable);
    }, 0);
  }

  trapFocus(elementId) {
    const element = this.querySelector(`#${elementId}`);
    const focusableEls = element.querySelectorAll(
      '& > div:first-child > ul > li > div:first-child a[href]:not([disabled]), & > div:first-child > ul > li > details > summary, & > ul > li > div:first-child a[href]:not([disabled])',
    );
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    element.addEventListener('keydown', function (e) {
      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
      if (!isTabPressed) return;

      if (e.shiftKey) {
        /* shift + tab */ if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } /* tab */ else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    });
  }

  toggleVideos(play) {
    play ? window.continuePlayingVideos() : window.pauseVideosPlaying();
  }

  toggleThirdPartyElements(hide) {
    if (hide) {
      document.body.classList.add('hide-third-party-elements');

      return;
    }

    document.body.classList.remove('hide-third-party-elements');
  }

  scrollTo(positionToY) {
    window.scroll({
      top: positionToY,
      left: 0,
      behavior: 'instant',
    });
  }

  fadeIn(element) {
    if (!element || element.isFadingIn) return;

    element.isFadingIn = true;
    element.isFadingOut = false;

    clearTimeout(element.fadeOutTimer);

    element.style.display = 'grid';
    element.style.transition = `opacity ${this.duration}ms`;
    element.style.opacity = 0;

    setTimeout(() => {
      window.requestAnimationFrame(() => {
        element.style.opacity = 1;
      });

      element.fadeInTimer = setTimeout(() => {
        element.style.transition = '';
        element.isFadingIn = false;
      }, this.duration);
    }, 1);
  }

  fadeOut(element) {
    if (!element || element.isFadingOut) return;

    element.isFadingOut = true;
    element.isFadingIn = false;

    clearTimeout(element.fadeInTimer);

    element.style.transition = `opacity ${this.duration}ms`;
    element.style.opacity = 1;

    window.requestAnimationFrame(() => {
      element.style.opacity = 0;
    });

    element.fadeOutTimer = setTimeout(() => {
      element.style.display = 'none';
      element.style.transition = '';
      element.isFadingOut = false;
    }, this.duration);
  }

  waitForElement(selector, callback, timeout = 20000) {
    const startTime = Date.now();

    function check() {
      const element = document.querySelector(selector);
      if (element) {
        callback(element);
      } else {
        if (Date.now() - startTime < timeout) {
          setTimeout(check, 100); // Check again after 100ms
        } else {
          // console.error(`Timeout: Element '${selector}' not found within ${timeout}ms`);
        }
      }
    }

    check();
  }
}

customElements.define('header-navigation', HeaderNavigation);

class DepartmentButtons extends HTMLElement {
  constructor() {
    super();
    this.buttons = this.querySelectorAll('button');

    if (this.buttons.length === 0) {
      return;
    }

    this.menus = document.querySelectorAll('mobile-menu ul[data-menu-name]');
  }

  connectedCallback() {
    this.setActiveDepartment();
    this.setupButtonListeners();
  }

  setActiveDepartment() {
    this.currentCookieValue = window.departmentPreference || this.buttons[0].dataset.type || 'mens';
    this.currentMenuName = window.menuPreference || cookies.getCookie('menuPreference') || this.buttons[0].dataset.menuName;
    if (this.currentCookieValue.toLowerCase() === 'kids') {
      this.currentCookieValue = 'mens';
    }
    this.setDepartmentClass(this.currentCookieValue);

    const departmentButtons = Array.from(this.buttons).filter(button => button.dataset.type === this.currentCookieValue);
    const departmentMenus = Array.from(this.menus).filter(menu => menu.dataset.department === this.currentCookieValue);

    this.buttons.forEach(button => button.classList.remove('active'));
    this.menus.forEach(menu => menu.classList.remove('active'));

    if (departmentButtons.length === 1) {
        departmentButtons[0].classList.add('active');
    } else {
        this.buttons.forEach((button) => {
            const isActive = button.dataset.menuName === this.currentMenuName;
            button.classList.toggle('active', isActive);
        });
    }

    if (departmentMenus.length === 1) {
        departmentMenus[0].classList.add('active');
    } else {
        this.menus.forEach((menu) => {
            const isActive = menu.dataset.menuName === this.currentMenuName;
            menu.classList.toggle('active', isActive);
        });
    }
  }

  setupButtonListeners() {
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const datasetActive = button.dataset.type;
        const datasetMenuName = button.dataset.menuName;

        this.setDepartmentClass(datasetActive);

        this.buttons.forEach((button) => {
          const isActive = button.dataset.menuName === datasetMenuName;
          button.classList.toggle('active', isActive);
        });

        this.menus.forEach((menu) => {
          menu.classList.toggle('active', menu.dataset.menuName === datasetMenuName);
        });

        cookies.setCookie('departmentPreference', datasetActive, 365, 'representclo.com');
        cookies.setCookie('menuPreference', datasetMenuName, 365, 'representclo.com');

        window.departmentPreference = datasetActive;
        window.menuPreference = datasetMenuName;
        window.dispatchEvent(new CustomEvent('department:change', { detail: { department: datasetActive, menu: datasetMenuName } }));
      });
    });
  }

  setDepartmentClass(datasetActive) {
    if (datasetActive === 'womens') {
      document.querySelector('header-navigation').classList.add('department-womens');
    } else {
      document.querySelector('header-navigation').classList.remove('department-womens');
    }
  }

}

customElements.define('department-buttons', DepartmentButtons);
